import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ThumbsDown,
  ThumbsUp,
  Star,
  Award,
  Calendar,
  Users,
  User,
  AlertCircle,
  AlertTriangle,
  BadgeAlert,
  CloudLightning,
  Flame,
  Wind,
  Fingerprint,
  FileWarning,
  Droplets,
  Zap,
  Thermometer,
  Milestone,
  HardDrive,
  UserMinus,
  Skull,
  ShieldAlert,
  ShieldX,
  AlertOctagon,
  Glasses,
  UserX,
  Briefcase,
  Music,
  Umbrella,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';

/**
 * Modal for displaying random events in the hotel simulation
 * 
 * @param {Object} event - The current random event to display
 * @param {function} onClose - Function to close the modal
 * @param {function} onAccept - Function to accept/handle the event (if choices are available)
 * @param {function} onDecline - Function to decline the event (if choices are available)
 * @param {string} eventMessage - Optional result message to display after event is handled
 * @param {boolean} hasChoice - Whether this event offers player choices
 * @param {number} day - Current game day
 */
const RandomEventModal = ({
  event,
  onClose,
  onAccept,
  onDecline,
  eventMessage = null,
  hasChoice = false,
  day = 1
}) => {
  const [showAnimation, setShowAnimation] = useState(false);
  const [showImpact, setShowImpact] = useState(false);
  const [adLoaded, setAdLoaded] = useState(false);

  useEffect(() => {
    // Add slight delay for better UX
    setTimeout(() => {
      setShowAnimation(true);
    }, 300);

    if (eventMessage) {
      setTimeout(() => {
        setShowImpact(true);
      }, 800);
    }

    // Initialize Google AdSense
    if (window.adsbygoogle) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        setAdLoaded(true);
      } catch (e) {
        console.error('AdSense error:', e);
      }
    }
  }, [eventMessage]);

  // Determine color scheme based on event type
  const getEventColorScheme = () => {
    if (!event || !event.id) return {
      gradientFrom: 'from-blue-800',
      gradientVia: 'via-blue-600',
      gradientTo: 'to-blue-500',
      borderColor: 'border-blue-700/20',
      iconBg: 'bg-blue-500',
      textColor: 'text-blue-100'
    };

    const eventId = event.id;

    // Positive events
    if (
      eventId.includes('conference') ||
      eventId.includes('celebrity') ||
      eventId.includes('festival') ||
      eventId.includes('review') ||
      (eventId.includes('competitor') && eventId.includes('Problem'))
    ) {
      return {
        gradientFrom: 'from-green-800',
        gradientVia: 'via-green-600',
        gradientTo: 'to-green-500',
        borderColor: 'border-green-700/20',
        iconBg: 'bg-green-500',
        textColor: 'text-green-100'
      };
    }

    // Severe negative events
    if (
      eventId.includes('hurricane') ||
      eventId.includes('fire') ||
      eventId.includes('tornado') ||
      eventId.includes('flooding') ||
      eventId.includes('breach') ||
      eventId.includes('inspection') ||
      eventId.includes('scandal') ||
      eventId.includes('evasion') ||
      eventId.includes('crime')
    ) {
      return {
        gradientFrom: 'from-red-800',
        gradientVia: 'via-red-600',
        gradientTo: 'to-red-500',
        borderColor: 'border-red-700/20',
        iconBg: 'bg-red-500',
        textColor: 'text-red-100'
      };
    }

    // Minor negative events
    if (
      eventId.includes('badWeather') ||
      eventId.includes('maintenance') ||
      eventId.includes('competitorUpgrade') ||
      eventId.includes('staffShortage')
    ) {
      return {
        gradientFrom: 'from-yellow-800',
        gradientVia: 'via-yellow-600',
        gradientTo: 'to-yellow-500',
        borderColor: 'border-yellow-700/20',
        iconBg: 'bg-yellow-500',
        textColor: 'text-yellow-100'
      };
    }

    // Default/neutral events
    return {
      gradientFrom: 'from-blue-800',
      gradientVia: 'via-blue-600',
      gradientTo: 'to-blue-500',
      borderColor: 'border-blue-700/20',
      iconBg: 'bg-blue-500',
      textColor: 'text-blue-100'
    };
  };

  const colorScheme = getEventColorScheme();

  // Helper to render icon based on event ID
  const renderEventIcon = () => {
    if (!event) return <AlertCircle className="w-8 h-8" />;

    // Use the icon from the event if available
    if (event.icon) {
      return React.cloneElement(event.icon, { className: "w-8 h-8" });
    }

    // Default icons based on event ID
    const eventId = event.id || '';
    
    if (eventId.includes('review')) return <ThumbsUp className="w-8 h-8" />;
    if (eventId.includes('celebrity')) return <Star className="w-8 h-8" />;
    if (eventId.includes('badWeather')) return <Umbrella className="w-8 h-8" />;
    if (eventId.includes('conference')) return <Briefcase className="w-8 h-8" />;
    if (eventId.includes('festival')) return <Music className="w-8 h-8" />;
    if (eventId.includes('maintenance')) return <AlertCircle className="w-8 h-8" />;
    if (eventId.includes('competitor')) {
      return eventId.includes('Upgrade') 
        ? <ArrowUp className="w-8 h-8" /> 
        : <ArrowDown className="w-8 h-8" />;
    }
    
    // Fallback for other events
    return <AlertTriangle className="w-8 h-8" />;
  };

  // Render impact summary with visuals
  const renderImpactSummary = () => {
    if (!eventMessage) return null;
    
    // Extract potential numerical changes from the message
    const moneyMatch = eventMessage.match(/\$([0-9,]+)/);
    const percentMatch = eventMessage.match(/([0-9.]+)%/);
    const reputationMatch = eventMessage.match(/reputation (?:dropped|increased) by ([0-9.]+)/i);
    const roomsMatch = eventMessage.match(/([0-9]+) rooms?/i);
    const guestsMatch = eventMessage.match(/([0-9]+) guests?/i);
    
    const hasMoney = moneyMatch && moneyMatch[1];
    const hasPercent = percentMatch && percentMatch[1];
    const hasReputation = reputationMatch && reputationMatch[1];
    const hasRooms = roomsMatch && roomsMatch[1];
    const hasGuests = guestsMatch && guestsMatch[1];
    
    return (
      <div className="mt-4 space-y-2">
        {hasMoney && (
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
              <span className="text-green-600 font-bold">$</span>
            </div>
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Financial Impact</div>
              <div className="font-medium">{moneyMatch[0]}</div>
            </div>
          </div>
        )}
        
        {hasReputation && (
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center mr-3">
              <Star className="w-4 h-4 text-yellow-600" />
            </div>
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Reputation Change</div>
              <div className="font-medium">
                {eventMessage.includes("dropped") 
                  ? <span className="text-red-500">-{reputationMatch[1]} stars</span> 
                  : <span className="text-green-500">+{reputationMatch[1]} stars</span>}
              </div>
            </div>
          </div>
        )}
        
        {hasRooms && (
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
              <Users className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Rooms Affected</div>
              <div className="font-medium">{roomsMatch[1]} rooms</div>
            </div>
          </div>
        )}
        
        {hasGuests && (
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center mr-3">
              <User className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Guests Affected</div>
              <div className="font-medium">{guestsMatch[1]} guests</div>
            </div>
          </div>
        )}
        
        {hasPercent && (
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-3">
              <span className="text-purple-600 font-bold">%</span>
            </div>
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Percentage Change</div>
              <div className="font-medium">{percentMatch[0]}</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <AnimatePresence>
      {/* Backdrop */}
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Modal Container */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 20, stiffness: 200 }}
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden border ${colorScheme.borderColor}`}
        >
          {/* Header */}
          <div className={`bg-gradient-to-br ${colorScheme.gradientFrom} ${colorScheme.gradientVia} ${colorScheme.gradientTo} p-6 text-center relative overflow-hidden`}>
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="eventPattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M0 20 L40 20" stroke="white" strokeWidth="0.5" />
                    <path d="M20 0 L20 40" stroke="white" strokeWidth="0.5" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#eventPattern)" />
              </svg>
            </div>

            {/* Day indicator */}
            <div className="absolute top-3 left-3 px-2 py-1 bg-black bg-opacity-30 rounded-md flex items-center">
              <Calendar className="w-3 h-3 mr-1.5 text-white" />
              <span className="text-xs text-white font-medium">Day {day}</span>
            </div>

            {/* Event icon */}
            <motion.div
              className="mb-4 relative z-10 mx-auto text-white"
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, type: 'spring', damping: 12 }}
            >
              <div className={`w-20 h-20 rounded-full ${colorScheme.iconBg} bg-opacity-20 backdrop-blur-sm flex items-center justify-center mx-auto border-2 border-white border-opacity-30`}>
                {renderEventIcon()}
              </div>
            </motion.div>

            {/* Title */}
            <motion.h2
              className="text-2xl font-bold text-white mb-2 drop-shadow-lg relative z-10"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, type: 'spring', damping: 12 }}
            >
              {event?.title || 'Random Event'}
            </motion.h2>

            {/* Description */}
            <motion.p
              className="text-white/90 relative z-10 text-base"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {event?.description || 'Something unexpected has happened!'}
            </motion.p>
          </div>

          {/* Content Area */}
          <div className="px-6 pt-6 pb-4">
            {/* Event Details/Impact */}
            <div className="mb-6">
              {eventMessage ? (
                <motion.div
                  className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg"
                  initial={{ opacity: 0, y: 10 }}
                  animate={showImpact ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                  transition={{ delay: 0.2 }}
                >
                  <h3 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">Event Impact</h3>
                  <p className="text-gray-700 dark:text-gray-300">{eventMessage}</p>
                  
                  {/* Visual representation of impact */}
                  {renderImpactSummary()}
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={showAnimation ? { opacity: 1 } : { opacity: 0 }}
                  className="text-gray-700 dark:text-gray-300"
                >
                  <p>This event requires your attention. How would you like to proceed?</p>
                </motion.div>
              )}
            </div>


            {/* Decision Area or Close Button */}
            <motion.div
              className={`flex ${hasChoice ? 'justify-between' : 'justify-center'} gap-3 mt-6`}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
          
                <button
                  onClick={onClose}
                  className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 active:from-blue-700 active:to-blue-600 text-white rounded-lg font-medium transition-all flex items-center justify-center shadow-md hover:shadow-lg active:shadow-sm"
                  type="button"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Continue
                </button>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RandomEventModal;